import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { RollNumberConfigService } from '../../../services/roll-number-config.service';

@Component({
  selector: 'app-create-roll-number',
  imports: [SharedModule],
  templateUrl: './create-roll-number.component.html',
  styleUrl: './create-roll-number.component.scss'
})
export class CreateRollNumberComponent {
  @Input() type: string = 'Create';
  @Input() rollNumberConfigData: any;
  @Input() allDepartments: any[] = [];
  @Input() allAcademicYears: any[] = [];
  @Output() closeDialog = new EventEmitter();
  @Output() reload = new EventEmitter();

  isSubmitted = false;
  btnLoading = false;

  rollNumberConfigForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private api: RollNumberConfigService,
    private localStorage: LocalStorageService
  ) {
    this.rollNumberConfigForm = this.fb.group({
      department_id: [null, [Validators.required]],
      academic_year_id: [null, [Validators.required]],
      prefix: ['', [Validators.required, Validators.maxLength(20)]],
      counter: [0, [Validators.required, Validators.min(0)]],
      length: [3, [Validators.required, Validators.min(1), Validators.max(10)]]
    });
  }

  ngOnInit() {
    if (this.type === 'Update' && this.rollNumberConfigData) {
      this.setFormData();
    }
  }

  setFormData() {
    this.rollNumberConfigForm.patchValue({
      department_id: this.rollNumberConfigData.department_id,
      academic_year_id: this.rollNumberConfigData.academic_year_id,
      prefix: this.rollNumberConfigData.prefix,
      counter: this.rollNumberConfigData.counter,
      length: this.rollNumberConfigData.length
    });
  }

  onSubmit() {
    this.isSubmitted = true;
    
    if (this.rollNumberConfigForm.invalid) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Please fill all required fields correctly.' 
      });
      return;
    }

    this.btnLoading = true;

    const formData = {
      ...this.rollNumberConfigForm.value,
      organization_id: this.localStorage.getOrganizationId(),
      campus_id: this.localStorage.getCampusId()
    };

    if (this.type === 'Update') {
      formData.roll_number_config_id = this.rollNumberConfigData.roll_number_config_id;
      this.updateRollNumberConfig(formData);
    } else {
      this.createRollNumberConfig(formData);
    }
  }

  createRollNumberConfig(formData: any) {
    this.api.createRollNumberConfig(formData).subscribe({
      next: (res: any) => {
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Roll number configuration created successfully.' 
        });
        this.btnLoading = false;
        this.closeDialog.emit();
        this.reload.emit();
      },
      error: (error: any) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: error?.error?.message || 'Failed to create roll number configuration.' 
        });
        this.btnLoading = false;
      }
    });
  }

  updateRollNumberConfig(formData: any) {
    this.api.updateRollNumberConfig(formData).subscribe({
      next: (res: any) => {
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Roll number configuration updated successfully.' 
        });
        this.btnLoading = false;
        this.closeDialog.emit();
        this.reload.emit();
      },
      error: (error: any) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: error?.error?.message || 'Failed to update roll number configuration.' 
        });
        this.btnLoading = false;
      }
    });
  }

  onCancel() {
    this.closeDialog.emit();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.rollNumberConfigForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched || this.isSubmitted));
  }

  getFieldError(fieldName: string): string {
    const field = this.rollNumberConfigForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required.`;
      if (field.errors['min']) return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['min'].min}.`;
      if (field.errors['max']) return `${this.getFieldLabel(fieldName)} must be at most ${field.errors['max'].max}.`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} must be at most ${field.errors['maxlength'].requiredLength} characters.`;
    }
    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'department_id': 'Department',
      'academic_year_id': 'Academic Year',
      'prefix': 'Prefix',
      'counter': 'Counter',
      'length': 'Length'
    };
    return labels[fieldName] || fieldName;
  }
}
